import { useEffect, useState } from "react";
import { Reveal, Eyebrow, H2, Section } from "./ui";
import { BookOpen, Video } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { WA_GUIDE, WA_SESSION, PRICE, SESSION_PRICE, trackWhatsAppClick, waLink } from "@/lib/api";
import { useCookieConsent } from "@/components/CookieConsentContext";
import { withAffiliateCode } from "@/components/affiliate/AffiliateAttribution";
import CheckoutButton from "@/components/landing/CheckoutButton";

const VALUE = [["Dudas sobre los ejercicios por WhatsApp durante 21 días", "incluido"], ["Reto guiado de 21 días", "incluido"], ["5 planificadores imprimibles", "incluido"], ["Módulo complementario para la pareja", "incluido"], ["Acceso al material sin fecha límite", "incluido"]];
const WA_BIZUM = waLink("Hola, quiero comprar Equipo en Calma por Bizum. ¿Me indicáis cómo hacerlo?");

export const GuiaGratis = () => {
  const { consent } = useCookieConsent();

  return (
    <Section id="guia-gratis" testid="free-guide-offer" className="bg-[var(--cream-2)]">
      <div className="max-w-3xl">
        <Eyebrow>Un recurso gratuito</Eyebrow>
        <H2>7 pasos para cuidar la pareja durante la crianza.</H2>
        <p className="mt-4 text-lg text-[#5a4f46]">Una guía breve con ideas y preguntas para hablar del cansancio, el reparto de tareas y los momentos de tensión, sin buscar hacerlo todo perfecto.</p>
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <Link to="/guia-pareja-crianza" data-testid="free-guide-read-button" className="btn-ghost"><BookOpen size={18} /> Leer y guardar la guía</Link>
          <a href={WA_GUIDE} target="_blank" rel="noreferrer" data-testid="free-guide-whatsapp-button" className="text-sm font-semibold underline underline-offset-4" onClick={() => trackWhatsAppClick("free-guide", consent)}>Prefiero pedirla por WhatsApp</a>
        </div>
        <p className="mt-3 text-xs text-[#5a4f46]">La guía es pública y gratuita. Si escribes por WhatsApp, responderemos a tu solicitud; no implica suscribirte a comunicaciones comerciales.</p>
      </div>
    </Section>
  );
};

export const RabietasTeaser = () => (
  <Section id="taller-rabietas" testid="rabietas-teaser" className="bg-[var(--cream-2)]">
    <div className="max-w-3xl">
      <Eyebrow>Otro taller de En Dos Mundos</Eyebrow>
      <H2>Rabietas infantiles: límites y emociones entre los 2 y 3 años.</H2>
      <p className="mt-4 text-lg text-[#5a4f46]">Un recorrido psicoeducativo independiente de 7 días para entender la etapa, responder a la frustración y preparar límites seguros.</p>
      <Link to={withAffiliateCode("/taller-rabietas-2-3")} data-testid="rabietas-teaser-link" className="btn-ghost mt-6">Conocer el taller · 19 €</Link>
    </div>
  </Section>
);

export const Precio = () => {
  return (
  <Section id="precio" testid="section-precio">
    <Reveal>
      <div className="max-w-3xl mx-auto rounded-[2.5rem] bg-white border-2 border-[var(--ink)] p-8 sm:p-12 shadow-[10px_10px_0_var(--ink)]">
        <Eyebrow>Pack Equipo en Calma</Eyebrow>
        <H2>Un programa práctico de 21 días para volver a hacer equipo.</H2>
        <div className="mt-6 grid sm:grid-cols-2 gap-x-8" data-testid="price-block">
          <p className="font-display text-5xl sm:text-6xl text-[var(--accent)]" data-testid="current-price">{PRICE} €</p>
          <p className="self-center text-sm text-[#5a4f46]">Pago único. Incluye {VALUE.map(([title]) => title.toLowerCase()).join(", ")}.</p>
        </div>
        <p className="mt-3 text-sm text-[#5a4f46]">En Dos Mundos · Sara Álvarez Navarro</p>
        <div className="mt-5 rounded-2xl bg-[var(--cream-2)] p-4" data-testid="included-psychological-support">
          <p className="font-semibold">Acompañamiento del equipo durante el taller</p>
          <p className="mt-1 text-sm text-[#5a4f46]">Durante 21 días puedes enviar por WhatsApp dudas sobre las lecciones y cómo poner en práctica sus ejercicios. El equipo responde personalmente por texto o audio en un máximo de 48 horas laborables.</p>
          <p className="mt-2 text-xs text-[#5a4f46]">Es orientación psicoeducativa vinculada al contenido; no incluye terapia individual, diagnóstico ni atención de urgencias.</p>
        </div>
        <div className="mt-6 flex flex-col items-start gap-3">
          <CheckoutButton testId="price-whatsapp-cta-button" className="btn-accent w-full sm:w-auto justify-center text-lg">Quiero empezar ya · {PRICE} €</CheckoutButton>
        </div>
        <p className="text-sm text-[#5a4f46] mt-3 max-w-2xl" data-testid="price-payment-note">Pago único. Continúa desde el botón para completar tu compra.</p>
        <a href={WA_BIZUM} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm font-semibold underline">¿Prefieres pagar por Bizum? Consúltanos por WhatsApp.</a>
      </div>
    </Reveal>
  </Section>
  );
};

const FAQ = [
  ["No tengo tiempo", "Son 10 minutos al día y puedes hacerlo cuando quieras: en la siesta, al acostarle o en el transporte. Si un día no puedes, sigues al siguiente."],
  ["Mi pareja no quiere", "Puedes hacer el recorrido en solitario. Incluye ejercicios para observar la dinámica familiar y probar otras formas de comunicarte, sin exigir que tu pareja participe."],
  ["Mi hijo/a es un bebé", "El reto está pensado para familias con hijos/as de 0 a 5 años. El cansancio, el sueño y el reparto de tareas son clave desde el primer día."],
  ["¿Qué apoyo profesional incluye?", "Durante los 21 días puedes consultar por WhatsApp dudas sobre las lecciones y la práctica de sus ejercicios. El equipo responde personalmente por texto o audio en un máximo de 48 horas laborables. Es orientación psicoeducativa sobre el taller, no terapia individual, diagnóstico ni atención de urgencias."],
  ["¿Cómo pago?", "Pulsa el botón para continuar con el pago. Si necesitas ayuda con el acceso después de la compra, contáctanos por WhatsApp."],
  ["¿Cuánto tiempo tengo acceso?", "Al material, para siempre: tu código no caduca y puedes repetir el reto cuando quieras. Las dudas por WhatsApp están incluidas durante los 21 días del reto."],
];

export const Faq = () => (
  <Section id="faq" testid="section-faq" className="bg-[var(--cream-2)]">
    <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
      <Reveal><Eyebrow>Preguntas frecuentes</Eyebrow><H2>Lo que suelen preguntarme.</H2></Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-b-2 border-[#d9cbb8]">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-lg font-semibold hover:no-underline py-5">{q}</AccordionTrigger>
              <AccordionContent className="text-base text-[#5a4f46]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </Section>
);

export const Sesion = () => (
  <Section id="sesion" testid="section-sesion" className="py-14 sm:py-16">
    <Reveal>
      <div className="rounded-3xl border-2 border-dashed border-[var(--sage)] p-7 sm:p-9 flex flex-col md:flex-row md:items-center gap-6">
        <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[#C9D8C5] shrink-0"><Video /></div>
        <div className="flex-1">
          <p className="font-display text-2xl">¿Necesitas una sesión online privada por videollamada?</p>
          <p className="text-[#5a4f46] mt-1">Sesión privada de 1 h por videollamada con Cristina Artamendi, psicóloga sanitaria · {SESSION_PRICE} €</p>
        </div>
        <a href={WA_SESSION} target="_blank" rel="noreferrer" data-testid="session-cta-button" className="btn-ghost">Reservar sesión</a>
      </div>
    </Reveal>
  </Section>
);

export const Cierre = () => {
  return (
  <Section id="cierre" testid="section-cierre" className="bg-[var(--terra)] text-white text-left">
    <Reveal>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">Podéis empezar con un paso pequeño.</h2>
      <p className="mt-4 text-lg max-w-xl text-white/90">Equipo en Calma: 21 días, ejercicios breves y acompañamiento por WhatsApp. Precio único: {PRICE} €.</p>
      <CheckoutButton testId="closing-cta-button" className="btn-terracotta mt-7 text-lg">Quiero empezar ya</CheckoutButton>
    </Reveal>
  </Section>
  );
};

export const StickyWhatsApp = () => {
  const [visible, setVisible] = useState(false);

  // Solo aparece cuando el CTA del hero ya no está en pantalla.
  useEffect(() => {
    const hero = document.querySelector('[data-testid="hero-cta-button"]');
    if (!hero || !("IntersectionObserver" in window)) { setVisible(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setVisible(!e.isIntersecting && e.boundingClientRect.top < 0), { threshold: 0 });
    io.observe(hero);
    return () => io.disconnect();
  }, []);

  return (
    <div aria-hidden={!visible} className={`md:hidden fixed bottom-0 inset-x-0 z-50 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-[var(--cream)]/95 border-t border-[#e3d6c4] transition-transform duration-200 ${visible ? "translate-y-0" : "translate-y-full pointer-events-none"}`}>
      <CheckoutButton testId="sticky-whatsapp-button" className="btn-accent w-full">Quiero empezar · {PRICE} €</CheckoutButton>
    </div>
  );
};
